import { useState, useEffect } from "react";
import PageTitle from "../components/PageTitle";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Collapse,
  Grid2,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { api, Game } from "../../shared/api/client";
import useCanCreateGames from "../hooks/useCanCreateGames";
import { Link, useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DiscordIcon from "../../shared/components/DiscordIcon";
import Chip from "@mui/material/Chip";
import { brand } from "../../shared/util/branding";

const botInstallUrl = import.meta.env.VITE_BOT_INSTALL_URL;
const botName =
  import.meta.env.VITE_BOT_NAME ||
  brand.name.toLowerCase().replace(/\s+/g, "-");

function BotInstallCard() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const command = `/${botName} library thread_channel:`;

  if (!botInstallUrl) return null;

  return (
    <Card elevation={1} sx={{ mb: 4, p: 3 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ p: 0.75, borderRadius: 1.5, background: `${theme.palette.secondary.main}22`, display: "flex" }}>
            <DiscordIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
          </Box>
          <Typography sx={{ fontWeight: "bold" }}>
            Host games on your Discord server
          </Typography>
        </Stack>
        <ExpandMoreIcon
          sx={{
            color: theme.palette.text.secondary,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      </Box>
      <Collapse in={open}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Play these games in your own Discord server with your friends.
          </Typography>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              1. Install the bot
            </Typography>
            <Button
              variant="contained"
              size="small"
              href={botInstallUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ background: "#5865F2", "&:hover": { background: "#4752C4" } }}
            >
              Add to Discord
            </Button>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              2. Set up a game library
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: brand.colors.footerBackground,
                p: 1.5,
                borderRadius: 1,
              }}
            >
              <Box
                component="code"
                sx={{ flex: 1, fontSize: "0.85rem", wordBreak: "break-all", whiteSpace: "pre-wrap" }}
              >
                {command}
              </Box>
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(command);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  sx={{ flexShrink: 0, color: theme.palette.text.secondary, "&:hover": { color: theme.palette.text.primary } }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, mt: 0.5, display: "block" }}
            >
              This creates a library where players can browse and start games.
              Matches will be created as threads in the channel you specify.
            </Typography>
          </Box>
        </Stack>
      </Collapse>
    </Card>
  );
}

function GameCard({ game }: { game: Game }) {
  const theme = useTheme();

  return (
    <Card
      component={Link}
      to={`/game/${game.id}`}
      sx={{
        textDecoration: "none",
        color: "inherit",
        background: theme.palette.background.paper,
        cursor: "pointer",
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.3)`,
        },
      }}
    >
      {game.gameplayImageUrl ? (
        <CardMedia
          component="img"
          height="160"
          image={game.gameplayImageUrl}
          alt={game.name}
          sx={{ objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}44 0%, ${theme.palette.primary.main}44 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" sx={{ opacity: 0.3 }}>
            {game.name.charAt(0)}
          </Typography>
        </Box>
      )}
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {game.boxArtUrl && (
            <Box
              component="img"
              src={game.boxArtUrl}
              alt={`${game.name} box art`}
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                objectFit: "cover",
                border: `1px solid rgba(255, 255, 255, 0.1)`,
              }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                {game.name}
              </Typography>
              {game.visibility && game.visibility !== "public" && (
                <Chip
                  label={game.visibility.charAt(0).toUpperCase() + game.visibility.slice(1)}
                  size="small"
                  color={
                    game.visibility === "private" ? "warning" :
                    game.visibility === "archived" ? "error" : "default"
                  }
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20 }}
                />
              )}
            </Stack>
            {game.minPlayers && game.maxPlayers && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PeopleIcon
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {game.minPlayers === game.maxPlayers
                    ? `${game.minPlayers} players`
                    : `${game.minPlayers}-${game.maxPlayers} players`}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Games() {
  const canCreateGames = useCanCreateGames();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getGames()
      .then((data) => {
        setGames(data.games);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box>
        <PageTitle title="Game Library" />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageTitle title="Game Library" />
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle title="Game Library" />
      <BotInstallCard />
      <Grid2 container spacing={3}>
        {games.map((game) => (
          <Grid2 key={game.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <GameCard game={game} />
          </Grid2>
        ))}
      </Grid2>

      {canCreateGames && (
        <Box sx={{ pt: 5 }}>
          <Button variant="contained" onClick={() => navigate("/creategame")}>
            Create Game
          </Button>
        </Box>
      )}
    </Box>
  );
}
